/**
 * Reads the `?f=`/`?view=` shared-filter-link query params
 * (specs/shareable-context-links.md § Route and params), decodes `?f=`, and
 * exposes shared-link mode as one piece of state its caller
 * (`ContextShellOrganism`) interprets. Surface-agnostic — knows nothing about
 * TreeMap, contexts, or levels; that interpretation (does the link address
 * this context, did the requested view resolve) lives in the shell, which has
 * the context/view data this hook does not.
 *
 * `?view=` is read independently of `?f=` — an absent `?f=` still yields
 * `requestedViewSystemName` when `?view=` is present (`activatesSharedModeOnlyWhenTheFilterParamIsPresent`).
 *
 * **The URL is the state, not a seed for it.** `active`/`filters` are derived
 * from the live `?f=` param on every render rather than latched at mount.
 * That matters because a page can mount several shells (`MetaspaceGrid`) and
 * there is only ever *one* `?f=`: with mount-once per-instance state, N shells
 * held N independent copies of one piece of page state, so `exit()` on one of
 * them deleted the param while its same-context siblings stayed in link mode —
 * still suppressing the reader's own filter layers with nothing in the URL
 * backing it. Deriving from the param means one `exit()` deactivates every
 * shell at once, because there is only one thing to deactivate.
 *
 * Nothing here writes `?f=`/`?view=` back to the URL except `exit()` (drop
 * `?f=`) and the one-shot clear of a malformed `?f=`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { SharedFilterLinkEnvelope } from 'types/context';
import { decodeFilterLink, hasVariableToken } from '../utils/filterLink';

const FILTER_PARAM = 'f';
const VIEW_PARAM = 'view';

/**
 * One-shot ownership of a malformed `?f=`, at module scope because the param
 * is *page* state while this hook is *per-shell*. Every mounted shell decodes
 * the same URL, so without an owner each one would clear the param and render
 * its own copy of the same "this link is malformed" notice — N boards, N
 * identical warnings for one bad link. The first subscriber to see a given
 * payload owns it; the rest report nothing.
 *
 * Keyed by the raw payload rather than a boolean so a *different* malformed
 * link arriving later still gets a notice.
 */
let malformedNoticeOwnedPayload: string | null = null;

/** @returns true if this caller now owns the notice for `raw`. */
function claimMalformedNotice(raw: string): boolean {
  if (malformedNoticeOwnedPayload === raw) return false;
  malformedNoticeOwnedPayload = raw;
  return true;
}

/**
 * Hands the claim back when its owner goes away. Without this, an owner that
 * unmounts (a board removed from a metaspace layout, a route change that keeps
 * one shell and drops another) takes the notice with it and no surviving shell
 * can report the link — the param is already gone from the URL by then.
 */
function releaseMalformedNotice(raw: string): void {
  if (malformedNoticeOwnedPayload === raw) malformedNoticeOwnedPayload = null;
}

/**
 * Test-only. Clears the module-scope malformed-notice claim so cases that
 * mount a malformed link do not depend on execution order.
 */
export function resetMalformedNoticeClaim(): void {
  malformedNoticeOwnedPayload = null;
}

export interface UseSharedFilterLinkReturn {
  /** True while a well-formed `?f=` is present in the URL. */
  active: boolean;
  /** The decoded envelope (with its D-3 `contextSystemName` binding) while `active`; `undefined` otherwise. */
  filters: SharedFilterLinkEnvelope | undefined;
  /**
   * Opaque identity of the link currently in the URL — the raw `?f=` payload.
   * A caller that needs to decide something *once per link* (e.g. which shell
   * a link was addressed to at the moment it arrived) keys on this rather than
   * on mount, so a link that appears after mount is still decided freshly.
   */
  linkId: string | undefined;
  /** True when `filters` contains a filter-variable token (`@me`, ...) — see `SharedLinkBannerMolecule`'s `@`-token variant. */
  hasVariableToken: boolean;
  /**
   * True after this hook claimed a `?f=` that was present but failed to
   * decode. The param is cleared from the URL immediately; this flag is the
   * caller's one-shot signal to show a notice
   * (`clearsAMalformedFilterParamAndNotifiesRatherThanRendering`). At most one
   * mounted hook reports it for a given payload.
   */
  malformedNotice: boolean;
  /** The raw `?view=` value, read independently of `?f=`/`active`. */
  requestedViewSystemName: string | undefined;
  /** Drops `?f=` via `replace` (never `push`), which deactivates shared-link mode for every shell on the page. Never writes anything server-side. */
  exit: () => void;
}

export function useSharedFilterLink(): UseSharedFilterLinkReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  const raw = searchParams.get(FILTER_PARAM);
  const decoded = useMemo(() => (raw ? decodeFilterLink(raw) : null), [raw]);

  /** The payload this instance owns a malformed notice for; sticky past the param's removal. */
  const [malformedPayload, setMalformedPayload] = useState<string | null>(null);
  const claimedRef = useRef<string | null>(null);

  const dropFilterParam = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(FILTER_PARAM);
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  useEffect(() => {
    if (!raw || decoded) return;
    // Already mine — re-running (StrictMode, a re-render) must not hand the
    // claim to a sibling and silently drop this instance's notice.
    if (claimedRef.current === raw) return;
    if (!claimMalformedNotice(raw)) return;

    claimedRef.current = raw;
    setMalformedPayload(raw);
    dropFilterParam();
  }, [raw, decoded, dropFilterParam]);

  // Unmount only — a cleanup on the effect above would release the claim on
  // every dependency change and let a sibling re-claim a notice this instance
  // is still rendering.
  useEffect(
    () => () => {
      if (claimedRef.current) releaseMalformedNotice(claimedRef.current);
    },
    []
  );

  return {
    active: decoded !== null,
    filters: decoded ?? undefined,
    linkId: raw ?? undefined,
    hasVariableToken: decoded ? hasVariableToken(decoded) : false,
    malformedNotice: malformedPayload !== null,
    requestedViewSystemName: searchParams.get(VIEW_PARAM) ?? undefined,
    exit: dropFilterParam,
  };
}

export default useSharedFilterLink;
